'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'

type AppNode = {
  id: string
  name: string
  /** baby: Accent only: names + chrome, not trademark logos */
  accent: string
  accentSoft: string
  chrome: string
  bodyLabel: string
  typed: string
  sidebar?: boolean
}

const APP_NODES: AppNode[] = [
  {
    id: 'slack',
    name: 'Slack',
    accent: '#4A154B',
    accentSoft: 'rgba(74, 21, 75, 0.14)',
    chrome: '#product',
    bodyLabel: 'message',
    typed: 'Ship notes: typed from voice.',
    sidebar: true,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    accent: '#25D366',
    accentSoft: 'rgba(37, 211, 102, 0.14)',
    chrome: 'Chat',
    bodyLabel: 'reply',
    typed: 'On my way: sending the update.',
  },
  {
    id: 'gmail',
    name: 'Gmail',
    accent: '#EA4335',
    accentSoft: 'rgba(234, 67, 53, 0.14)',
    chrome: 'Compose',
    bodyLabel: 'body',
    typed: 'Thanks: draft ready for review.',
  },
  {
    id: 'docs',
    name: 'Docs',
    accent: '#4285F4',
    accentSoft: 'rgba(66, 133, 244, 0.14)',
    chrome: 'Untitled',
    bodyLabel: 'paragraph',
    typed: 'Meeting recap captured from speech.',
  },
  {
    id: 'notes',
    name: 'Notes',
    accent: '#F7C948',
    accentSoft: 'rgba(247, 201, 72, 0.18)',
    chrome: 'Scratch',
    bodyLabel: 'note',
    typed: 'Hold Shift · speak · release.',
  },
  {
    id: 'ide',
    name: 'IDE',
    accent: '#3B82F6',
    accentSoft: 'rgba(59, 130, 246, 0.14)',
    chrome: 'main.ts',
    bodyLabel: 'comment',
    typed: '// TODO: wire paste-at-cursor',
    sidebar: true,
  },
]

const AppChromeCard = memo(function AppChromeCard({
  app,
  index,
  reduceMotion,
}: {
  app: AppNode
  index: number
  reduceMotion: boolean
}) {
  return (
    <motion.article
      className="nink-hybrid-card overflow-hidden"
      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Title bar */}
      <div
        className="flex items-center gap-2 border-b border-[color:var(--ninxl-theme-border-soft,var(--ninxl-8bit-dim))] px-3 py-2"
        style={{ backgroundColor: app.accentSoft }}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: app.accent }}
          aria-hidden
        />
        <span className="ninxl-theme-text text-[10px] font-bold tracking-wider md:text-[11px]">
          {app.name}
        </span>
        <span className="ninxl-theme-text-muted ml-auto truncate text-[9px] md:text-[10px]">
          {app.chrome}
        </span>
      </div>

      {/* Fake app body */}
      <div className="flex min-h-[108px]">
        {app.sidebar ? (
          <div
            className="w-8 shrink-0 border-r border-[color:var(--ninxl-theme-border-soft,var(--ninxl-8bit-dim))] p-1.5 md:w-10"
            style={{ backgroundColor: app.accentSoft }}
            aria-hidden
          >
            <div className="mb-1 h-1 w-full opacity-40" style={{ backgroundColor: app.accent }} />
            <div className="mb-1 h-1 w-[75%] bg-[color:var(--ninxl-theme-fg)]/20" />
            <div className="h-1 w-[60%] bg-[color:var(--ninxl-theme-fg)]/15" />
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col gap-2 p-2.5">
          <div className="nink-hybrid-eyebrow !tracking-[0.1em] opacity-70">{app.bodyLabel}</div>

          {app.id === 'whatsapp' ? (
            <div className="space-y-1.5">
              <div className="nink-hybrid-rounded-sm ml-auto max-w-[85%] px-2 py-1 text-[10px] text-white md:text-[11px]"
                style={{ backgroundColor: app.accent }}
              >
                {app.typed}
                <span className="ninxl-8bit-blink ml-0.5 inline-block h-2.5 w-0.5 align-middle bg-white" />
              </div>
            </div>
          ) : app.id === 'gmail' ? (
            <div className="space-y-1.5 font-mono text-[10px] md:text-[11px]">
              <div className="ninxl-theme-text-muted">To: team@…</div>
              <div className="ninxl-theme-text-muted">Subject: Update</div>
              <div className="ninxl-theme-text border-t border-[color:var(--ninxl-theme-border-soft)] pt-1.5">
                {app.typed}
                <span
                  className="ninxl-8bit-blink ml-0.5 inline-block h-2.5 w-0.5 align-middle"
                  style={{ backgroundColor: app.accent }}
                />
              </div>
            </div>
          ) : (
            <p className="ninxl-theme-text font-mono text-[10px] leading-relaxed md:text-[11px]">
              {app.typed}
              <span
                className="ninxl-8bit-blink ml-0.5 inline-block h-2.5 w-0.5 align-middle"
                style={{ backgroundColor: app.accent }}
              />
            </p>
          )}

          <div className="mt-auto flex items-center gap-1.5 pt-1">
            <span
              className="ninxl-8bit-blink h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: app.accent }}
            />
            <span className="nink-hybrid-body text-[10px] ninxl-theme-text-muted md:text-[11px]">
              NINK → cursor
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  )
})

/**
 * baby: Where NINK writes: recognizable app chrome nodes (not hero).
 */
export const NinkWorksInSection = memo(function NinkWorksInSection() {
  const reduceMotion = Boolean(useReducedMotion())

  return (
    <div className="mx-auto max-w-5xl px-6">
      <NinxlScrollReveal>
        <div className="mb-10 text-center">
          <div className="nink-hybrid-eyebrow mb-3 justify-center">Works in</div>
          <h2 className="nink-hybrid-display ninxl-theme-text mx-auto mb-4 max-w-2xl text-2xl md:text-4xl">
            Slack. WhatsApp. Gmail. Docs. Wherever the cursor is.
          </h2>
          <p className="nink-hybrid-body mx-auto max-w-xl text-sm md:text-base">
            NINK does not open its own editor. It pastes into the frontmost app: chat, mail, docs,
            notes, or your IDE: so writing stays where work already happens.
          </p>
        </div>
      </NinxlScrollReveal>

      {/* Hub node: NINK tray → apps */}
      <NinxlScrollReveal delay={0.05}>
        <div className="nink-hybrid-card mx-auto mb-8 flex max-w-lg flex-col items-center gap-3 px-5 py-5 text-center">
          <div className="nink-hybrid-chip">
            <span
              className="ninxl-8bit-blink h-1.5 w-1.5 rounded-full bg-[color:var(--ninxl-brand-teal)]"
              aria-hidden
            />
            NINK tray · Dictate
          </div>
          <div className="nink-hybrid-body text-sm font-medium ninxl-theme-text">
            Speak once → types in
          </div>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {APP_NODES.map((app) => (
              <span
                key={app.id}
                className={cn('nink-hybrid-chip text-[11px] md:text-xs')}
                style={{
                  borderColor: app.accent,
                  backgroundColor: app.accentSoft,
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: app.accent }}
                  aria-hidden
                />
                {app.name}
              </span>
            ))}
          </div>
        </div>
      </NinxlScrollReveal>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {APP_NODES.map((app, index) => (
          <AppChromeCard
            key={app.id}
            app={app}
            index={index}
            reduceMotion={reduceMotion}
          />
        ))}
      </div>

      <NinxlScrollReveal delay={0.1}>
        <p className="nink-hybrid-status mt-8 text-center">
          Any app with a text cursor · Mac + Windows
        </p>
      </NinxlScrollReveal>
    </div>
  )
})
